import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';

function ProductEditModal({ open, onClose, product, onSave, isSaving }) {
  const [editedProduct, setEditedProduct] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [attributeName, setAttributeName] = useState('');
  const [attributeValue, setAttributeValue] = useState('');

  const isNewProduct = !product?.id;

  const attributes = useLiveQuery(
    () => product?.id ? db.productAttributes.where('prodotto_id').equals(product.id).toArray() : [],
    [product?.id]
  );

  useEffect(() => {
    if (product) {
      // Mapping fallbacks per supportare vecchi campi
      setEditedProduct({
        ...product,
        nome: product.nome || product.name || '',
        descrizione_breve: product.descrizione_breve || product.description || '',
        descrizione_lunga: product.descrizione_lunga || '',
        prezzo: product.prezzo || product.price || '',
        imageUrl: product.imageUrl || '',
        sku: product.sku || '',
        slug: product.slug || '',
        visibile: product.visibile === 1 || product.visibile === true,
        in_evidenza: product.in_evidenza === 1 || product.in_evidenza === true,
      });
    } else {
      setEditedProduct(null);
    }
    setIsDirty(false);
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setEditedProduct(prev => ({ ...prev, [name]: finalValue }));
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave(editedProduct);
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const handleAddAttribute = async () => {
    if (!attributeName || !attributeValue || !product?.id) return;
    await db.productAttributes.add({
      prodotto_id: product.id,
      name: attributeName,
      value: attributeValue,
    });
    setAttributeName('');
    setAttributeValue('');
  };

  const handleDeleteAttribute = async (id) => {
    await db.productAttributes.delete(id);
  };

  if (!editedProduct) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNewProduct ? 'Crea Nuovo Prodotto' : 'Modifica Prodotto'}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          <TextField
            fullWidth
            margin="normal"
            label="Nome Prodotto"
            name="nome"
            value={editedProduct.nome || ''}
            onChange={handleChange}
            autoFocus={isNewProduct}
            disabled={isSaving}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="SKU"
              name="sku"
              value={editedProduct.sku || ''}
              onChange={handleChange}
              disabled={isSaving}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Slug"
              name="slug"
              value={editedProduct.slug || ''}
              onChange={handleChange}
              disabled={isSaving}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 4, mt: 1, mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="visibile"
                  checked={!!editedProduct.visibile}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              }
              label="Visibile"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="in_evidenza"
                  checked={!!editedProduct.in_evidenza}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              }
              label="In Evidenza"
            />
          </Box>

          <TextField
            fullWidth
            margin="normal"
            label="Descrizione Breve"
            name="descrizione_breve"
            value={editedProduct.descrizione_breve || ''}
            onChange={handleChange}
            multiline
            rows={2}
            disabled={isSaving}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Descrizione Lunga"
            name="descrizione_lunga"
            value={editedProduct.descrizione_lunga || ''}
            onChange={handleChange}
            multiline
            rows={4}
            disabled={isSaving}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Prezzo"
            name="prezzo"
            value={editedProduct.prezzo || ''}
            onChange={handleChange}
            disabled={isSaving}
          />
          <TextField
            fullWidth
            margin="normal"
            label="URL Immagine"
            name="imageUrl"
            value={editedProduct.imageUrl || ''}
            onChange={handleChange}
            disabled={isSaving}
          />
        </Box>

        {!isNewProduct && (
          <Box sx={{ mt: 4, opacity: isSaving ? 0.5 : 1 }}>
            <Divider />
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Attributi del Prodotto
            </Typography>
            <fieldset disabled={isSaving}>
              <List dense>
                {attributes?.map(attr => (
                  <ListItem
                    key={attr.id}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAttribute(attr.id)}>
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={attr.name} secondary={attr.value} />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  label="Nome Attributo"
                  value={attributeName}
                  onChange={(e) => setAttributeName(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Valore Attributo"
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  size="small"
                />
                <Button onClick={handleAddAttribute} variant="outlined" size="small">
                  Aggiungi
                </Button>
              </Box>
            </fieldset>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={handleClose} color="secondary" disabled={isSaving}>
          Annulla
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={(!isNewProduct && !isDirty) || isSaving}
        >
          {isSaving ? 'Salvataggio...' : (isNewProduct ? 'Crea' : 'Salva Modifiche')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export default ProductEditModal;
